import React from "react";
import { motion } from "framer-motion";

const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/1244c932-ba4e-452c-af76-86755415d04f/images/df7aca2f669c13bc5724fd87d81455ea6b1ad23ecb77a10d7845576312d2b670.png";

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.7 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  },
};

const Hero = () => (
  <section
    data-testid="hero-section"
    id="hero"
    style={{
      position: "relative",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      overflow: "hidden",
      backgroundColor: "#030303",
    }}
  >
    {/* Background image with scale-in animation */}
    <motion.div
      initial={{ scale: 1.08, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "brightness(0.22) saturate(1.3)",
      }}
    />

    {/* Gradient overlays */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(135deg, rgba(3,3,3,0.7) 0%, rgba(3,3,3,0.4) 50%, rgba(3,3,3,0.75) 100%)",
      }}
    />
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "220px",
        background: "linear-gradient(to bottom, transparent, #030303)",
      }}
    />
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "160px",
        background: "linear-gradient(to top, transparent, rgba(3,3,3,0.5))",
      }}
    />

    {/* Content */}
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      style={{
        position: "relative",
        zIndex: 1,
        padding: "clamp(80px, 10vw, 160px) clamp(24px, 6vw, 96px) clamp(60px, 8vw, 120px)",
      }}
    >
      {/* Overline */}
      <motion.div variants={fadeUp} style={{ marginBottom: "clamp(20px, 3vw, 44px)" }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.28em",
            color: "#FF3B00",
            textTransform: "uppercase",
          }}
        >
          ANANTORA — MMXXV
        </span>
      </motion.div>

      {/* Main heading */}
      <motion.div variants={fadeUp} style={{ marginBottom: "clamp(16px, 2vw, 32px)" }}>
        <h1
          data-testid="hero-title"
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(3.8rem, 14.5vw, 20rem)",
            lineHeight: 0.88,
            letterSpacing: "-0.04em",
            color: "#F4F4F0",
            textTransform: "uppercase",
            margin: 0,
          }}
        >
          ANANTORA
        </h1>
      </motion.div>

      {/* Red separator */}
      <motion.div
        variants={fadeUp}
        style={{
          width: "56px",
          height: "2px",
          background: "#FF3B00",
          marginBottom: "clamp(20px, 3vw, 44px)",
        }}
      />

      {/* Tagline + description */}
      <motion.div
        variants={fadeUp}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          maxWidth: "760px",
        }}
      >
        <p
          data-testid="hero-tagline"
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "clamp(1.5rem, 3vw, 3rem)",
            color: "#F4F4F0",
            margin: 0,
            letterSpacing: "-0.025em",
            lineHeight: 1.05,
            textShadow: "0 2px 40px rgba(0,0,0,0.9)",
          }}
        >
          Software that endures.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "440px" }}>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px",
              color: "#888888",
              lineHeight: 1.8,
              margin: 0,
            }}
          >
            We build original software products — focused, purposeful, and
            designed to outlast the competition.
          </p>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            {["Products", "GRC", "SaaS"].map((t) => (
              <span
                key={t}
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "9px",
                  color: "#3A3A3A",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2.5, duration: 1.2 }}
      style={{
        position: "absolute",
        bottom: "40px",
        left: "clamp(24px, 6vw, 96px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <motion.div
        animate={{ scaleY: [1, 0.2, 1], opacity: [0.8, 0.3, 0.8] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: "1px",
          height: "64px",
          background: "linear-gradient(to bottom, #FF3B00, transparent)",
          transformOrigin: "top",
        }}
      />
    </motion.div>

    {/* Bottom-right corner tag */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2.8, duration: 1 }}
      style={{
        position: "absolute",
        bottom: "44px",
        right: "clamp(24px, 6vw, 96px)",
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "9px",
          color: "#2A2A2A",
          letterSpacing: "0.2em",
        }}
      >
        anantora.com
      </span>
    </motion.div>
  </section>
);

export default Hero;
